import styled from '@emotion/styled'
import { ECards, ESessionPhase, TBoxBodyPublic, TSessionBodyPublic, TSessionPublic } from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import { css } from '@emotion/react'
import BetStatusIndicators from './BetStatusIndicators'
import BoxEvents from './BoxEvents'
import Card from './Card'
import { ReactComponent as AddCardIcon } from './img/addCardIcon.svg'
import { ReactComponent as InfoIcon } from './img/info.svg'
import { green, red, yellow } from './Notice'
import { useRussianPokerStore } from './useRussianPokerStore'

const getBoxColor = (box: TBoxBodyPublic, session: TSessionBodyPublic): 'yellow' | 'green' | 'red' => {
  if (session.phase === ESessionPhase.DEALING) return 'green'
  if (box.result.outcome === 'win') return 'green'
  if (box.result.outcome === 'lose') return 'red'
  if (box.result.outcome === 'tie') return 'yellow'
  if (box.result.outcome === 'noGame') return 'red'
  if (box.ante < 0) return 'red'
  return 'yellow'
}

const getBoxBorderColor = (color: 'yellow' | 'green' | 'red') => {
  switch (color) {
    case 'green':
      return 'rgba(139, 229, 137, 0.50)'
    case 'red':
      return 'rgba(237, 28, 36, 0.50)'
    default:
      return 'rgba(255, 196, 69, 0.50)'
  }
}
const getBoxTextColor = (color: 'yellow' | 'green' | 'red') => {
  switch (color) {
    case 'green':
      return `${green}`
    case 'red':
      return `${red}`
    default:
      return `${yellow}`
  }
}
const getBoxNumberColor = (color: 'yellow' | 'green' | 'red') => {
  switch (color) {
    case 'green':
      return 'rgba(139, 229, 137, 0.15)'
    case 'red':
      return 'rgba(237, 28, 36, 0.15)'
    default:
      return 'rgba(255, 196, 69, 0.15)'
  }
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getIconFill = (color: 'yellow' | 'green' | 'red') => {
  switch (color) {
    case 'green':
      return `${green}`
    case 'red':
      return `${red}`
    default:
      return `${yellow}`
  }
}

const StyledBoxes = styled.div<{
  boxOnTurn: TSessionPublic['boxOnTurn']
  size: number
}>`
  display: grid;
  padding: 30px 0;
  gap: 10px;
  grid-template-columns: ${(props) => (props.size === 1 ? '1fr' : '1fr 1fr')};
  height: max-content;

  ${({ boxOnTurn, size }) => {
    if (size === 1) return css``

    if (boxOnTurn === 1) {
      return `
        grid-template-columns: 2fr 1fr;
      `
    }
    if (boxOnTurn === 2) {
      return `
        grid-template-columns: 1fr 2fr;
      `
    }
    return `
      grid-template-columns: 1fr 1fr;
    `
  }}
`

const StyledBox = styled.div<{
  decision?: boolean
  color?: 'yellow' | 'green' | 'red'
}>`
  border-radius: 10px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%);
  position: relative;
  padding: 25px 20px;
  border: 4px solid ${({ color = 'yellow' }) => getBoxBorderColor(color)};
  color: ${({ color = 'yellow' }) => getBoxTextColor(color)};

  ${({ decision }) => {
    if (decision === undefined) return `opacity: 1`
    return `opacity: ${decision ? 1 : 0.5};`
  }}
`

const StyledBoxInfo = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 40px auto;
`

const StyledInfoIcon = styled(InfoIcon)<{
  color?: 'yellow' | 'green' | 'red'
}>`
  width: 40px;
  height: 40px;
`

const StyledBoxNumber = styled.div<{
  color?: 'yellow' | 'green' | 'red'
}>`
  color: ${({ color = 'yellow' }) => getBoxNumberColor(color)};
  font-size: 80px;
  font-style: normal;
  font-weight: 510;
  font-variant: small-caps;
  letter-spacing: -1.6px;
  position: absolute;
  right: 10px;
  top: 0;
  line-height: 1;
`

const CardsContainer = styled.div`
  gap: 10px;
  padding: 20px 0;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
`

const CardItem = styled.div<{ isSwappingCard?: boolean }>`
  display: flex;
  flex: 0 0 auto;
  width: clamp(50px, calc((100% - 50px) / 6), 90px); /* минимум 60px, максимум 90px */
  aspect-ratio: 9/14;
  justify-self: center;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.29);
  box-shadow: 0 75px 189px 0 rgba(255, 255, 255, 0.12) inset;
  backdrop-filter: blur(3px);
  position: relative;

  ${({ isSwappingCard }) =>
    isSwappingCard &&
    `
    transform: translateY(-20px);
    border: 3px solid rgba(255, 196, 69, 0.5);
  `}
`

const CardItemSix = styled(CardItem)`
  border: 3px solid rgba(255, 196, 69, 0.5);
  box-shadow: 0 75px 189px 0 rgba(255, 255, 255, 0.12) inset;
  backdrop-filter: blur(3px);
  align-items: center;
  justify-content: center;
`

const CardNumber = styled.div`
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 196, 69, 0.8);
  color: black;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`

const Boxes = observer(() => {
  const { boxes, session, game } = useRussianPokerStore()

  if (!game) return null
  if (!session) return null

  const isResultPhases = [ESessionPhase.SETTLEMENT, ESessionPhase.FINISHED].includes(session.phase)
  const isDecisionsPhase = session.phase === ESessionPhase.DECISIONS
  const activeBoxes = boxes.filter((box) => {
    if (session.phase === ESessionPhase.BETTING) return true

    return box.ante
  })

  return (
    <StyledBoxes boxOnTurn={session.boxOnTurn} size={activeBoxes.length}>
      {activeBoxes.map((box) => {
        const card1 = box.cards[0]
        const card2 = box.cards[1]
        const card3 = box.cards[2]
        const card4 = box.cards[3]
        const card5 = box.cards[4]
        const card6 = box.cards[5]

        const isPendingSixCard = isDecisionsPhase && box.pendingCardsRequest === 'buySix'
        const isPendingSwitchCard = isDecisionsPhase && box.pendingCardsRequest === 'switch'

        const boxColor = getBoxColor(box, session)

        return (
          <StyledBox
            decision={
              session.phase === ESessionPhase.DECISIONS && session.boxOnTurn !== null
                ? session.boxOnTurn === box.seatNumber
                : undefined
            }
            key={box.seatNumber}
            color={boxColor}>
            <StyledBoxNumber color={boxColor}>{box.seatNumber}</StyledBoxNumber>
            <StyledBoxInfo>
              <StyledInfoIcon fill={getIconFill(boxColor)} />
              <BoxEvents box={box} game={game} session={session} />
            </StyledBoxInfo>
            <CardsContainer>
              {/* Card 1 */}
              <CardItem isSwappingCard={isPendingSwitchCard && card1 === ECards.XX}>
                {card1 && <Card value={isResultPhases ? card1 : 'XX'} />}
                {isPendingSwitchCard && card1 === ECards.XX && <CardNumber>1</CardNumber>}
              </CardItem>

              {/* Card 2 */}
              <CardItem isSwappingCard={isPendingSwitchCard && card2 === ECards.XX}>
                {card2 && <Card value={isResultPhases ? card2 : 'XX'} />}
                {isPendingSwitchCard && card2 === ECards.XX && <CardNumber>2</CardNumber>}
              </CardItem>

              {/* Card 3 */}
              <CardItem isSwappingCard={isPendingSwitchCard && card3 === ECards.XX}>
                {card3 && <Card value={isResultPhases ? card3 : 'XX'} />}
                {isPendingSwitchCard && card3 === ECards.XX && <CardNumber>3</CardNumber>}
              </CardItem>

              {/* Card 4 */}
              <CardItem isSwappingCard={isPendingSwitchCard && card4 === ECards.XX}>
                {card4 && <Card value={isResultPhases ? card4 : 'XX'} />}
                {isPendingSwitchCard && card4 === ECards.XX && <CardNumber>4</CardNumber>}
              </CardItem>

              {/* Card 5 */}
              <CardItem isSwappingCard={isPendingSwitchCard && card5 === ECards.XX}>
                {card5 && <Card value={isResultPhases ? card5 : 'XX'} />}
                {isPendingSwitchCard && card5 === ECards.XX && <CardNumber>5</CardNumber>}
              </CardItem>

              {/* Card 6 */}
              {card6 && (
                <CardItem isSwappingCard={isPendingSwitchCard && card6 === ECards.XX}>
                  {card6 && <Card value={isResultPhases ? card6 : 'XX'} />}
                  {isPendingSwitchCard && card6 === ECards.XX && <CardNumber>6</CardNumber>}
                </CardItem>
              )}

              {/* Pending Six Card */}
              {isPendingSixCard && (
                <CardItemSix>
                  <AddCardIcon />
                </CardItemSix>
              )}
            </CardsContainer>

            <BetStatusIndicators box={box} />
          </StyledBox>
        )
      })}
    </StyledBoxes>
  )
})

export default Boxes
